import React, { useState } from "react";
import { Box, Typography, Button, Menu, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import useNavigateToTop from "../../../hooks/general/useNavigateToTop";
import BracketGameInfo from "../GameInfoComponents/BracketGameInfo";
import SurvivorGameInfo from "../GameInfoComponents/SurvivorGameInfo";
import QuickPickGameInfo from "../GameInfoComponents/QuickPickGameInfo";
import "./MyPoolsCardNew.scss";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

/**
 * Determine the picks status for all entries
 * @param {Array} entries - Array of entry objects
 * @param {String} gameType - Type of game (Bracket, Survivor, QuickPick)
 * @param {Number|null} currentInterval - Current interval if applicable
 * @param {Number|null} picksToMake - Number of picks that should be made
 * @return {String} - 'all', 'some', 'none', or 'eliminated' (for Survivor)
 */
const checkPicksStatus = (entries, gameType, currentInterval, picksToMake) => {
  // Handle empty entries
  if (!entries || entries.length === 0 || !picksToMake) {
    return null;
  }

  // Track entries with picks and eliminations (for Survivor)
  let entriesWithPicks = 0;
  let entriesWithCompletePicks = 0;
  let allEntriesEliminated = true;
  
  // Check each entry
  for (const entry of entries) {
    const pool = entry.pool || {};
    const picks = pool.picks || [];
    
    // For Survivor type, check elimination status
    if (gameType === "Survivor") {
      // Check if any pick has a loss
      const hasLoss = picks.some(pick => pick.result === "loss");
      
      // If at least one entry doesn't have a loss, not all are eliminated
      if (!hasLoss) {
        allEntriesEliminated = false;
      }
      
      // Now check pick completion for this entry
      if (currentInterval) {
        // Count picks for the current interval
        const intervalPicksCount = picks.filter(pick => pick.interval === currentInterval).length;
        if (intervalPicksCount > 0) entriesWithPicks++;
        if (intervalPicksCount === picksToMake) entriesWithCompletePicks++;
      } else {
        // No interval specified, count total picks
        if (picks.length > 0) entriesWithPicks++;
        if (picks.length === picksToMake) entriesWithCompletePicks++;
      }
    } 
    // For non-Survivor games
    else {
      if (currentInterval) {
        // Count picks for the current interval
        const intervalPicksCount = picks.filter(pick => pick.interval === currentInterval).length;
        if (intervalPicksCount > 0) entriesWithPicks++;
        if (intervalPicksCount === picksToMake) entriesWithCompletePicks++;
      } else {
        // No interval specified, count total picks
        if (picks.length > 0) entriesWithPicks++;
        if (picks.length === picksToMake) entriesWithCompletePicks++;
      }
    }
  }
  
  // Return elimination status for Survivor games if all entries are eliminated
  if (gameType === "Survivor" && allEntriesEliminated) {
    return 'eliminated';
  }
  
  // Return pick status
  if (entriesWithCompletePicks === entries.length) {
    return 'all';
  } else if (entriesWithPicks > 0) {
    return 'some';
  } else {
    return 'none';
  }
};

const MyPoolsCard = ({ gameData, myPoolsData, active }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigateToTop = useNavigateToTop();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    };

    const handleGoToPool = (gameAddress, masterPoolId, gameId) => {
        const url = `/${uriEncoded(gameAddress)}?mId=${masterPoolId}&gId=${gameId}&page=default`;
        navigateToTop(url);
        setAnchorEl(null); // Close dropdown after selection
    };

    const handleButtonClick = (event) => {
        if (gameIds && gameIds.length > 1) {
            setAnchorEl(event.currentTarget);
        } else if (gameIds && gameIds.length === 1) {
            handleGoToPool(gameAddress, masterPoolId, gameIds[0].gameId);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const myPoolsDataForThisGame = myPoolsData?.find(pool => pool.masterPoolId === gameData.masterPoolId) || null;
    
    const gameType = myPoolsDataForThisGame?.gameType;
    const picksToMake = myPoolsDataForThisGame?.picksToMake;
    const currentInterval = myPoolsDataForThisGame?.currentInterval;
    const entries = gameData?.entries || [];
    
    // Calculate pick status using our new function
    const pickStatus = checkPicksStatus(entries, gameType, currentInterval, picksToMake);
    //console.log("Pick Status:", pickStatus, "Picks To Make:", picksToMake, "Current Interval:", currentInterval, "entries:", entries);
    
    if (!gameData) return null;

    const { masterPoolName, masterPoolLogo, gameIds, gameAddress, masterPoolId } = gameData;

    // Helper to determine status indicator props
    const getStatusIndicator = () => {
        switch(pickStatus) {
            case 'all':
                return {
                    text: 'Picks Made',
                    color: '#2BD64D' // Green for complete
                };
            case 'some':
                return {
                    text: 'Incomplete Picks',
                    color: '#FF6B0E' // Orange for in progress
                };
            case 'none':
                return {
                    text: 'No Picks Made',
                    color: '#FFC60A' // Red for no picks
                };
            case 'eliminated':
                return {
                    text: 'Eliminated',
                    color: '#CC293C' // Gray for eliminated
                };
            default:
                return {
                    text: '',
                    color: 'transparent'
                };
        }
    };

    const statusProps = getStatusIndicator();
    //console.log("Status Props:", statusProps);
    
    return (
        <Box 
            className="my-pools-new-card-container"    
        >
            <Box>
                <img 
                    src={masterPoolLogo}
                    alt="Pool Logo"
                    className='my-pools-new-card-logo'
                />
            </Box>

            <Box>
                <Typography className="my-pools-new-card-title">
                    {masterPoolName}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box className="my-pools-new-card-button-container">
                        <Button
                            className="my-pools-new-card-button"
                            onClick={handleButtonClick}
                            endIcon={gameIds && gameIds.length > 1 ? <KeyboardArrowDownIcon /> : <ArrowRightAltIcon />}
                            sx={{
                                width: '100px',
                                justifyContent: 'center',
                                '& .MuiButton-endIcon': {
                                    marginLeft: '4px', // Adjust spacing between text and icon
                                    marginRight: 0
                                }
                            }}
                        >
                            <Box sx={{ paddingLeft: '4px' }}>
                                {gameIds && gameIds.length > 1 ? 'Select Pool' : 'Go To Pool'}
                            </Box>
                        </Button>
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            MenuListProps={{
                                'aria-labelledby': 'select-pool-button',
                                sx: { 
                                    padding: '0px'
                                }
                            }}
                            PaperProps={{
                                sx: {
                                    width: '250px',
                                    maxWidth: '250px'
                                }
                            }}
                        >
                            {gameIds && gameIds.map((game) => (
                                <MenuItem 
                                    key={game.gameId} 
                                    onClick={() => handleGoToPool(gameAddress, masterPoolId, game.gameId)}
                                    sx={{
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        padding: '8px 12px',
                                        justifyContent: 'center'
                                    }}
                                >
                                    {game.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                    {active && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    color: '#8E8E8E',
                                }}
                            >
                                {statusProps.text}
                            </Typography>
                            <Box
                                sx={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: statusProps.color,
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default MyPoolsCard;